import api, { toSnake, runDurableJob } from './api';
import type { TeachingContext } from './teaching-context';
import {defaultSelectionBook} from './catalog-kind';

export const selectionApi = {
  books: () => api.get('/selection/textbooks'),
  createBook: (body: any) => api.post('/selection/textbooks', toSnake(body)),
  chapters: (id: number) => api.get(`/selection/textbooks/${id}/chapters`),
  saveChapters: (id: number, body: any) => api.put(`/selection/textbooks/${id}/chapters`, toSnake(body)),
  links: (id: number, body: any) => api.post(`/selection/textbooks/${id}/links`, toSnake(body)),
  knowledge: (subject: string, grade: string, textbookVersion?: string, coursewareId?: string|number) => api.get('/selection/knowledge', {params:{subject,grade,textbook_version:textbookVersion,courseware_id:coursewareId}}),
  questions: (body: any) => api.post('/selection/questions', toSnake(body)),
  workspace: () => api.get('/selection/workspace'),
  saveWorkspace: (body: any) => api.put('/selection/workspace', toSnake(body)),
  check: (body: any) => api.post('/selection/check', toSnake(body)),
  compose: (body: any) => runDurableJob('selection_compose', body, body.requestKey),
  paper: (id: number) => api.get(`/selection/papers/${id}`),
  replace: (id: number, body: any) => api.post(`/selection/papers/${id}/replace`, toSnake(body)),
  updatePaper: (id: number, items: any[], expectedItems: any[]) => api.put(`/selection/papers/${id}/items`, toSnake({items,expectedItems})),
};

export const questionFormats = [
  {value:'single_choice',label:'单选题'}, {value:'multi_choice',label:'多选题'},
  {value:'fill_blank',label:'填空题'}, {value:'short_answer',label:'解答题'},
  {value:'true_false',label:'判断题'}, {value:'essay',label:'论述题'},
];
export const formatName = (value: string) => questionFormats.find((item) => item.value === value)?.label || value;
export const difficultyName: Record<string,string> = {easy:'容易',medium:'适中',hard:'困难'};
export const emptyScope = (subject='数学',grade='三年级') => ({subject,grade,textbookId:undefined as number|undefined,
  textbookVersion:undefined as string|undefined,coursewareId:undefined as number|undefined,
  chapterIds:[] as string[],knowledgeIds:[] as string[],knowledgeMatch:'any',keyword:'',questionFormat:undefined as string|undefined,
  difficulty:undefined as string|undefined,region:undefined as string|undefined,year:undefined as number|undefined,
  source:undefined as string|undefined,unassigned:false,sort:'latest'});

const versionName = (value?:string) => ['统编版','部编版'].includes(value||'') ? '统编版（部编版）' : value||'';
const sourceId = (value?:string|number) => value ? Number(value) : undefined;
export const selectionTeachingKey = (value:any) => JSON.stringify([value.subject,value.grade,versionName(value.textbookVersion),sourceId(value.coursewareId)]);
export const questionMatchesTeaching = (question:any,scope:any) => question.subject===scope.subject && question.grade===scope.grade && versionName(question.textbookVersion)===versionName(scope.textbookVersion);
export const matchingSelectionBooks = (books:any[],teaching:TeachingContext) => books.filter(book =>
  ['verified','framework'].includes(book.status) && book.subject===teaching.subject && book.grade===teaching.grade && versionName(book.textbookVersion)===versionName(teaching.textbookVersion));

/** 全局范围覆盖历史工作区/URL中的教学范围；只保留仍然有效的局部选择。 */
export function inheritSelectionTeaching(config:any, teaching:TeachingContext, books:any[]) {
  const old=config.scope;
  const changed=selectionTeachingKey(old)!==selectionTeachingKey(teaching);
  const matching=matchingSelectionBooks(books,teaching);
  const linked=matching.filter(book => book.coursewareIds?.some((id:any)=>sourceId(id)===sourceId(teaching.coursewareId)));
  const retained=!changed && matching.find(book=>book.id===old.textbookId);
  const chosen=retained || (linked.length===1 ? linked[0] : defaultSelectionBook(matching));
  const bookChanged=old.textbookId!==chosen?.id;
  const title=changed && (!config.title || [old.subject+'章节练习',old.subject+'练习'].includes(config.title)) ? teaching.subject+'练习' : config.title;
  return {...config,title,withinBook:changed||!chosen?false:config.withinBook,scope:{...old,
    subject:teaching.subject,grade:teaching.grade,textbookVersion:teaching.textbookVersion,coursewareId:sourceId(teaching.coursewareId),textbookId:chosen?.id,
    ...(changed?{knowledgeIds:[],keyword:''}:{}),
    ...(changed||bookChanged?{chapterIds:[],unassigned:false}:{}),
  }};
}

/** 目录和知识点保留各自选择，但请求只使用当前可见的范围，避免隐藏条件串题。 */
export const activeSelectionScope = (scope: any, rangeMode: string, withinBook: boolean, smart=false) => ({
  ...scope,
  textbookId: rangeMode === 'chapter' || withinBook ? scope.textbookId : undefined,
  chapterIds: rangeMode === 'chapter' ? scope.chapterIds : [],
  knowledgeIds: rangeMode === 'knowledge' ? scope.knowledgeIds : [],
  unassigned: !smart && rangeMode === 'chapter' && scope.unassigned,
  ...(smart ? {difficulty:undefined,questionFormat:undefined,keyword:'',source:undefined,region:undefined,year:undefined} : {}),
});

export function selectionSearch(scope: any, rangeMode: string, withinBook: boolean) {
  const params = new URLSearchParams();
  ['subject','grade','textbookVersion','coursewareId','textbookId','keyword','questionFormat','difficulty','knowledgeMatch','region','year','source','sort'].forEach((key) => {
    if (scope[key] != null && scope[key] !== '') params.set(key,String(scope[key]));
  });
  if (scope.chapterIds?.length) params.set('chapters',scope.chapterIds.join(','));
  if (scope.knowledgeIds?.length) params.set('knowledge',scope.knowledgeIds.join(','));
  params.set('range',rangeMode); params.set('withinBook',String(withinBook));
  if (scope.unassigned) params.set('unassigned','true');
  return params.toString();
}

export function restoreSelectionSearch(search: string, fallback: any) {
  const p = new URLSearchParams(search);
  if (!p.size) return fallback;
  const scope = {...fallback.scope};
  ['subject','grade','textbookVersion','keyword','questionFormat','difficulty','knowledgeMatch','region','source','sort'].forEach((key) => {
    if (p.has(key)) scope[key] = p.get(key) || undefined;
  });
  scope.textbookId = p.get('textbookId') ? Number(p.get('textbookId')) : undefined;
  scope.coursewareId = p.get('coursewareId') ? Number(p.get('coursewareId')) : undefined;
  scope.year = p.get('year') ? Number(p.get('year')) : undefined;
  scope.chapterIds = p.get('chapters')?.split(',').filter(Boolean) || [];
  scope.knowledgeIds = p.get('knowledge')?.split(',').filter(Boolean) || [];
  scope.unassigned = p.get('unassigned') === 'true';
  return {...fallback,scope,rangeMode:p.get('range') === 'knowledge' ? 'knowledge' : 'chapter',withinBook:p.get('withinBook') === 'true'};
}


/** 断网或刷新后的同参数重试复用请求编号，避免模型仍在运行时重复保存试卷。 */
export function selectionRequestKey(owner: string|number, body: any) {
  const canonical=(value:any):any=>Array.isArray(value)?value.map(canonical):value&&typeof value==='object'?Object.fromEntries(Object.keys(value).sort().map(key=>[key,canonical(value[key])])):value;
  const payload=JSON.stringify(canonical(body));const storageKey=`edupaper:selection-request:${owner}`;
  try {const old=JSON.parse(sessionStorage.getItem(storageKey)||'null');if(old?.payload===payload)return old.key;}catch{}
  const key=crypto.randomUUID();sessionStorage.setItem(storageKey,JSON.stringify({payload,key}));return key;
}
export function clearSelectionRequest(owner: string|number) { sessionStorage.removeItem(`edupaper:selection-request:${owner}`); }
